import React, { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ErrorReference } from '../error/error-reference'
import '../sso-workspace/sso-workspace.scss'
import '../../layout/authorized/org/api-keys/api-keys.scss'
import './scim-dialogs.scss'

// The frame of the SCIM link dialogs that ask first (owner 2026-10-29: the
// token dialogs "look crappy"): the reveal-once dialog's frame
// (secret-dialog.js) -- a header with a short title and the connection under
// it, a body, a footer -- so the dialogs that make a credential and the one
// that shows it read alike. The footer carries a live summary of what will
// happen, in the viewer's zone, above the buttons (Cancel, then the action
// named), right-aligned. Escape and the backdrop are Cancel (nothing is
// lost: nothing has been made yet), and focus starts on Cancel, the safe
// choice. The page behind is dimmed (.sw-scrim). Wide on a desktop; a
// full-screen sheet with a sticky header and footer on a phone.
export const ScimFormDialog = ({ open, title, subtitle, intro, children, summary, error, failure, busy, confirmText, confirmClassName = 'btn sw-btn-primary', confirmDisabled, onConfirm, onCancel, testId }) => {
  const titleId = useId()
  const introId = useId()
  const cancelRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); onCancel() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  useEffect(() => {
    if (open && cancelRef.current) cancelRef.current.focus({ preventScroll: true })
  }, [open])

  if (!open) return null

  return createPortal(
    <div className="sw-drawer sw-scrim justify-content-center align-items-center" data-testid="scim-dialog-backdrop"
      onClick={e => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="sw-dialog ak-secret-dialog ak-secret-dialog-lg sd-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}
        aria-describedby={intro ? introId : undefined} data-testid={testId}>
        <header className="ak-dialog-head">
          <h2 className="h5 m-0" id={titleId}>{title}</h2>
          {subtitle && <p className="sd-subtitle" data-testid="scim-dialog-subtitle">{subtitle}</p>}
        </header>
        <div className="ak-dialog-body">
          {intro && <p className="sd-intro" id={introId}>{intro}</p>}
          {children}
        </div>
        <footer className="ak-dialog-foot sd-foot">
          {summary && <p className="sd-summary" aria-live="polite" data-testid="scim-dialog-summary">{summary}</p>}
          {error &&
            <div className="sd-error">
              <p className="text-danger small m-0" role="alert">{error}</p>
              <ErrorReference error={failure} compact />
            </div>}
          <div className="sw-dialog-actions">
            <button type="button" className="btn sw-btn" ref={cancelRef} onClick={onCancel}>Cancel</button>
            <button type="button" className={confirmClassName} disabled={busy || !!confirmDisabled} onClick={onConfirm}>{confirmText}</button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  )
}

// One labelled choice of a dialog: the label names the group (a radio group
// for a SegmentedChoice), `hint` is one quiet line under the control.
export const DialogField = ({ id, label, hint, children }) =>
  <section className="sd-field" aria-labelledby={id}>
    <h3 className="sd-label" id={id}>{label}</h3>
    {children}
    {hint && <p className="sd-hint">{hint}</p>}
  </section>

// "What happens", as short numbered steps.
export const DialogSteps = ({ title = 'What happens', steps }) =>
  <section className="sd-field">
    <h3 className="sd-label">{title}</h3>
    <ol className="sd-steps">
      {steps.map(step => <li key={typeof step === 'string' ? step : step.key}>{typeof step === 'string' ? step : step.text}</li>)}
    </ol>
  </section>
